import type { ReactElement } from "react";
import { Link } from "react-router";

import { useSearchForm } from "../../forms/use-search-form.js";

import { adminCenterUrls } from "./urls.js";

/** 探す条件（サーバの CenterMailSearchViewModel の条件の欄）。 */
export interface MailConditions {
    readonly keyword: string | null;
    readonly tenantId: number | null;
    /** yyyy-MM-dd（DateOnly）。 */
    readonly fromDate: string | null;
    readonly toDate: string | null;
    /** MailSendStatus（Core）の数値。 */
    readonly status: number | null;
    /** MailDeliveryStatus（Core）の数値。 */
    readonly delivery: number | null;
}

/** MailSendStatus（Core）の値。<b>数値は C# の列挙と同じ。</b> */
const sendStatuses = [
    { value: 1, label: "受け付けられた" },
    { value: 2, label: "失敗" },
    { value: 0, label: "不明" },
    { value: 3, label: "送らない設定" },
] as const;

/** MailDeliveryStatus（Core）の値。<b>数値は C# の列挙と同じ。</b> */
const deliveryStatuses = [
    { value: 3, label: "届いた" },
    { value: 4, label: "宛先に断られた" },
    { value: 5, label: "送られなかった" },
    { value: 6, label: "迷惑メール報告" },
    { value: 2, label: "再送中" },
    { value: 0, label: "通知なし" },
] as const;

const text = (value: number | string | null): string => (value === null ? "" : String(value));

/** 送ったメールを探す窓。 */
export function MailSearchForm({ conditions }: { readonly conditions: MailConditions }): ReactElement {
    const form = useSearchForm(adminCenterUrls.mails, {
        Keyword: text(conditions.keyword), FromDate: text(conditions.fromDate), ToDate: text(conditions.toDate),
        TenantId: text(conditions.tenantId), Status: text(conditions.status), Delivery: text(conditions.delivery),
    });

    return (
        <section className="card mb-4 p-6">
            <form method="get" {...form.props}>
                <div className="mb-3">
                    <label htmlFor="Keyword" className="form-label">宛先・件名で探す</label>
                    <input
                        id="Keyword" name="Keyword" className="form-input" autoComplete="off"
                        defaultValue={text(conditions.keyword)} placeholder="メールアドレス、または件名の一部"
                    />
                    <p className="form-help">本文は探しません（記録が大きく、走査に時間がかかるため）。</p>
                </div>

                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label htmlFor="FromDate" className="form-label">この日から</label>
                        <input id="FromDate" name="FromDate" type="date" className="form-input" defaultValue={text(conditions.fromDate)} />
                    </div>
                    <div>
                        <label htmlFor="ToDate" className="form-label">この日まで</label>
                        <input id="ToDate" name="ToDate" type="date" className="form-input" defaultValue={text(conditions.toDate)} />
                    </div>
                    <div>
                        <label htmlFor="TenantId" className="form-label">契約 ID</label>
                        <input
                            id="TenantId" name="TenantId" type="number" className="form-input" autoComplete="off"
                            defaultValue={text(conditions.tenantId)}
                        />
                    </div>
                    <div>
                        <label htmlFor="Status" className="form-label">送信結果</label>
                        <select id="Status" name="Status" className="form-input" defaultValue={text(conditions.status)}>
                            <option value="">すべて</option>
                            {sendStatuses.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="Delivery" className="form-label">配送結果</label>
                        <select id="Delivery" name="Delivery" className="form-input" defaultValue={text(conditions.delivery)}>
                            <option value="">すべて</option>
                            {deliveryStatuses.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    {/* 送るボタンとクリアはひとまとまりにする（折り返すときも一緒に次の行へ落ちる。片方だけ別の行に分かれない） */}
                    <div className="flex gap-3">
                        <button type="submit" className="btn-primary">検索</button>
                        {/* **クリアは「条件の無い URL を開く」。**type="reset" は描いたときの値に戻るだけ */}
                        <Link to={adminCenterUrls.mails} className="btn-secondary">クリア</Link>
                    </div>
                </div>
                <p className="form-help">
                    「この日まで」はその日いっぱいを含みます。
                    <strong className="text-ink-800">「送信」は SendGrid へ渡せたか、「配送」は相手に届いたか</strong>です。
                </p>
            </form>
        </section>
    );
}
